import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dropdown } from './Dropdown';
import { BellRing, Clock, MoveRight, Check, CheckCheck, Megaphone, ShoppingBag, UserPlus, ClipboardList, AlertTriangle, Bell, X, RefreshCw, AlertCircle } from 'lucide-react';
import SimpleBar from 'simplebar-react';
import { bellNotificationService } from '../../../modules/common-modules/Notification/services/bellNotification.service';
import versionControlService from '../../services/versionControl.service';
import logger from '../../utils/logger';
import { showToast } from './CustomToast';

interface BellNotification {
  id: string;
  title: string;
  message: string;
  category: string;
  priority: string;
  isRead: boolean;
  readAt?: string;
  actionUrl?: string;
  referenceType?: string;
  referenceId?: string;
  senderName?: string;
  senderAvatar?: string;
  createdAt: string;
  icon?: string;
  iconColor?: string;
}

const categoryIcons: Record<string, React.ReactNode> = {
  announcement: <Megaphone className="size-5 text-blue-500" />,
  order: <ShoppingBag className="size-5 text-green-500" />,
  lead: <UserPlus className="size-5 text-purple-500" />,
  task: <ClipboardList className="size-5 text-orange-500" />,
  alert: <AlertTriangle className="size-5 text-red-500" />,
  system: <Bell className="size-5 text-gray-500" />,
};

const priorityColors: Record<string, string> = {
  urgent: 'bg-red-500',
  high: 'bg-orange-500',
  medium: 'bg-blue-500',
  low: 'bg-gray-400',
};

const NotificationDropdown: React.FC = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<BellNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await bellNotificationService.getRecentNotifications(10, 'web');
      if (response) {
        setNotifications(response.notifications || []);
        setUnreadCount(response.unreadCount || 0);

        // Push version info to version control service (for UpdateButton to consume)
        logger.debug('Notification API Response:', {
          hasVersionInfo: !!response.versionInfo,
          versionInfo: response.versionInfo
        });
        if (response.versionInfo) {
          versionControlService.setVersionInfo(response.versionInfo);
        }
      }
    } catch (err) {
      logger.error('Failed to fetch notifications:', err);
      setError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const handleNotificationClick = async (notification: BellNotification) => {
    try {
      if (!notification.isRead) {
        await bellNotificationService.markAsRead(notification.id);
        setNotifications(prev =>
          prev.map(n => n.id === notification.id ? { ...n, isRead: true } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      }

      if (notification.actionUrl) {
        navigate(notification.actionUrl);
      }
    } catch (error) {
      logger.error('Failed to mark notification as read:', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      setActionLoading('markAll');
      await bellNotificationService.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
      showToast.success('All notifications marked as read');
    } catch (err) {
      logger.error('Failed to mark all as read:', err);
      showToast.error('Failed to mark all as read');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDismiss = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      setActionLoading(id);
      await bellNotificationService.dismiss(id);
      const notification = notifications.find(n => n.id === id);
      setNotifications(prev => prev.filter(n => n.id !== id));
      if (notification && !notification.isRead) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      logger.error('Failed to dismiss notification:', err);
      showToast.error('Failed to dismiss notification');
    } finally {
      setActionLoading(null);
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <Dropdown className="relative flex items-center h-header">
      <Dropdown.Trigger
        type="button"
        className="inline-flex justify-center relative items-center p-0 text-topbar-item transition-all size-[37.5px] duration-200 ease-linear bg-topbar rounded-md dropdown-toggle btn hover:bg-topbar-item-bg-hover hover:text-topbar-item-hover group-data-[topbar=dark]:bg-topbar-dark group-data-[topbar=dark]:hover:bg-topbar-item-bg-hover-dark group-data-[topbar=dark]:hover:text-topbar-item-hover-dark group-data-[topbar=brand]:bg-topbar-brand group-data-[topbar=brand]:hover:bg-topbar-item-bg-hover-brand group-data-[topbar=brand]:hover:text-topbar-item-hover-brand group-data-[topbar=dark]:dark:bg-zink-700 group-data-[topbar=dark]:dark:hover:bg-zink-600 group-data-[topbar=brand]:text-topbar-item-brand group-data-[topbar=dark]:dark:hover:text-zink-50 group-data-[topbar=dark]:dark:text-zink-200 group-data-[topbar=dark]:text-topbar-item-dark"
        id="notificationDropdown"
      >
        <BellRing className="inline-block size-5 stroke-1 fill-slate-100 group-data-[topbar=dark]:fill-topbar-item-bg-hover-dark group-data-[topbar=brand]:fill-topbar-item-bg-hover-brand" />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 flex w-1.5 h-1.5">
            <span className="absolute inline-flex w-full h-full rounded-full opacity-75 animate-ping bg-sky-400"></span>
            <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-sky-500"></span>
          </span>
        )}
      </Dropdown.Trigger>

      <Dropdown.Content
        placement="right-end"
        className="absolute z-50 ltr:text-left rtl:text-right bg-white rounded-md shadow-md !top-4 dropdown-menu min-w-[20rem] lg:min-w-[26rem] dark:bg-zink-600"
        aria-labelledby="notificationDropdown"
      >
        <div className="p-4 flex items-center justify-between border-b border-slate-200 dark:border-zink-500">
          <h6 className="text-16 font-semibold">
            Notifications
            {unreadCount > 0 && (
              <span className="inline-flex items-center justify-center size-5 ml-2 text-[11px] font-medium border rounded-full text-white bg-orange-500 border-orange-500">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </h6>
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllRead}
              className="text-xs text-custom-500 hover:text-custom-600 flex items-center gap-1"
            >
              <CheckCheck className="size-3" />
              Mark all read
            </button>
          )}
        </div>

        <SimpleBar className="max-h-[350px]">
          {loading && notifications.length === 0 ? (
            <div className="p-8 text-center text-slate-500 dark:text-zink-300">
              <RefreshCw className="size-6 mx-auto mb-2 animate-spin opacity-50" />
              <p>Loading notifications...</p>
            </div>
          ) : error ? (
            <div className="p-8 text-center">
              <AlertCircle className="size-12 mx-auto mb-3 text-red-400" />
              <p className="text-sm text-red-500 dark:text-red-400 mb-3">{error}</p>
              <button
                onClick={fetchNotifications}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-custom-500 hover:bg-custom-50 dark:hover:bg-custom-500/10 rounded transition-colors"
              >
                <RefreshCw className="size-3" />
                Retry
              </button>
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-8 text-center text-slate-500 dark:text-zink-300">
              <BellRing className="size-12 mx-auto mb-3 opacity-30" />
              <p>No notifications yet</p>
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`relative flex gap-3 p-4 w-full text-left hover:bg-slate-50 dark:hover:bg-zink-500 transition-colors cursor-pointer ${
                    !notification.isRead ? 'bg-blue-50/50 dark:bg-zink-500/30' : ''
                  }`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="relative shrink-0">
                    <div className="flex items-center justify-center size-10 rounded-md bg-slate-100 dark:bg-zink-500">
                      {categoryIcons[notification.category] || categoryIcons.system}
                    </div>
                    {!notification.isRead && (
                      <div className={`absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full ${priorityColors[notification.priority] || priorityColors.medium}`} />
                    )}
                  </div>

                  <div className="grow min-w-0">
                    <h6 className={`mb-1 text-sm truncate ${!notification.isRead ? 'font-semibold' : 'font-medium'}`}>
                      {notification.title}
                    </h6>
                    <p className="text-xs text-slate-500 dark:text-zink-300 line-clamp-2 mb-1">
                      {notification.message}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-slate-400 dark:text-zink-400">
                      <Clock className="size-3" />
                      <span>{formatTime(notification.createdAt)}</span>
                      {notification.senderName && (
                        <>
                          <span>•</span>
                          <span>{notification.senderName}</span>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="shrink-0 self-center flex items-center gap-1">
                    {notification.isRead ? (
                      <Check className="size-4 text-green-500" />
                    ) : (
                      <div className="w-2 h-2 rounded-full bg-custom-500" />
                    )}
                    <button
                      onClick={(e) => handleDismiss(notification.id, e)}
                      disabled={actionLoading === notification.id}
                      className="p-1 ml-1 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded transition-colors opacity-0 group-hover:opacity-100 hover:opacity-100"
                      title="Dismiss"
                    >
                      <X className={`size-3 ${actionLoading === notification.id ? 'animate-spin' : ''}`} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </SimpleBar>

        <div className="flex items-center gap-2 p-4 border-t border-slate-200 dark:border-zink-500 justify-end">
          <div className="shrink-0">
            <button
              type="button"
              onClick={() => navigate('/notifications')}
              className="px-2 py-1.5 text-xs text-white transition-all duration-200 ease-linear btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100"
            >
              All Notifications
              <MoveRight className="inline-block size-3 ml-1" />
            </button>
          </div>
        </div>
      </Dropdown.Content>
    </Dropdown>
  );
};

export default NotificationDropdown;
